import { useState } from "react";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

export interface ThoughtStep {
  id: string;
  label: string;
  icon?: string;
  status: "active" | "complete" | "error";
  detail?: string;
}

interface ChainOfThoughtProps {
  steps: ThoughtStep[];
  className?: string;
}

function ChainOfThought({ steps, className }: ChainOfThoughtProps) {
  const [open, setOpen] = useState(true);
  if (steps.length === 0) return null;

  const activeStep = steps.find(step => step.status === "active");
  const summary = activeStep?.label ?? steps[steps.length - 1]?.label ?? "Completed";

  return (
    <div className={cn("w-full max-w-md rounded-lg border border-border bg-muted/20 overflow-hidden", className)}>
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={open}
      >
        <span className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background">
          {activeStep ? (
            <Icon name="refresh" size={11} className="animate-spin text-muted-foreground" />
          ) : (
            <Icon name="tick-01" size={11} className="text-emerald-500" />
          )}
        </span>
        <span className="flex-1 truncate font-mono text-[11px] uppercase tracking-widest text-foreground">
          {summary}
        </span>
        <Icon name="arrow-down-01" size={11} className={cn("text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="border-t border-border px-3 py-2">
          {steps.map((step, index) => (
            <div key={step.id} className="relative flex gap-2.5 py-2 last:pb-1">
              {index < steps.length - 1 && <span className="absolute left-[9px] top-7 bottom-[-2px] w-px bg-border" />}
              <span className={cn(
                "relative z-10 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border bg-background",
                step.status === "error" ? "border-red-400/40 text-red-400" :
                step.status === "active" ? "border-foreground/40 text-foreground" :
                "border-border text-muted-foreground"
              )}>
                <Icon
                  name={step.status === "active" ? "refresh" : step.status === "error" ? "alert-01" : step.icon ?? "tick-01"}
                  size={10}
                  className={step.status === "active" ? "animate-spin" : undefined}
                />
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn(
                  "font-sans text-xs leading-snug",
                  step.status === "active" ? "text-foreground" : "text-muted-foreground"
                )}>{step.label}</p>
                {step.detail && <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground/60">{step.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { ChainOfThought };